import { IMapInputHandler } from 'app/map/services/map_input_handling/MapInputHandler';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import { EventRegistry } from 'app/shared/events/event_registry';

export class MapInputHandlerCollection {
    private _mapInputHandlers: Array<IMapInputHandler>;
    private _cleanupCalledEventRegistry: CachedEventRegistry;
    private _activeMapInputHandlerChangedEventRegistry: EventRegistry;

    constructor() {
        this._mapInputHandlers = [];
        this._cleanupCalledEventRegistry = new CachedEventRegistry();
        this._activeMapInputHandlerChangedEventRegistry = new CachedEventRegistry();
    }

    public subscribeToActiveHandlerChange(
        callback: (handler: IMapInputHandler) => void,
    ): () => void {
        return this._activeMapInputHandlerChangedEventRegistry.addEventListener(callback);
    }

    public addMapInputHandler(mapInputHandler: IMapInputHandler): void {
        this._mapInputHandlers.push(mapInputHandler);
        const existingCleanup = mapInputHandler.cleanUp.bind(mapInputHandler);
        mapInputHandler.cleanUp = async () => {
            existingCleanup();
            await this._cleanupCalledEventRegistry.notifyListeners();
        };
    }

    public removeMapInputHandler(mapInputHandler: IMapInputHandler): void {
        if (mapInputHandler.active) {
            throw new Error("can't remove a input handler when it's active");
        }
        const index = this._mapInputHandlers.findIndex((h) => h.name === mapInputHandler.name);
        if (index !== -1) {
            this._mapInputHandlers.splice(index, 1);
        }
    }

    public getActiveHandler(): IMapInputHandler {
        return this._mapInputHandlers.find((h) => h.active === true);
    }

    public getHandlerByName(name: string): IMapInputHandler {
        return this._mapInputHandlers.find((h) => h.name === name);
    }

    public getDefaultHandler(): IMapInputHandler {
        return this._mapInputHandlers.find((h) => h.isDefault === true);
    }

    public subscribeToCleanupCalls(fn: () => void): void {
        this._cleanupCalledEventRegistry.addEventListener(fn);
    }

    public async setActiveHandler(handler: IMapInputHandler): Promise<void> {
        this._mapInputHandlers.forEach((h) => (h.active = false));
        handler.active = true;
        await this._activeMapInputHandlerChangedEventRegistry.notifyListeners(handler);
    }
}
